/**
 * Loan-purpose journey controller (WI-TV-W6-001).
 *
 * Wave 6 has two step journeys — purchase and refinance — that share one mount
 * point, one inbound prefill listener, and one destructive "Change loan type"
 * reset. This module owns that shared lifecycle so neither wizard has to know
 * about the other:
 *
 * - It mounts the journey the entry resolved (default/explicit purchase, or
 *   explicit refinance) at step 1.
 * - Every pre-submit step of either journey exposes `Change loan type`, which
 *   returns to the loan-purpose selector. That is the explicit destructive
 *   action: both journey models are dropped whole — answers, validation and
 *   error state, skipped-step markers, derived values, prefill-derived values,
 *   and all submission/retry/in-flight state — before the selector is shown.
 *   Choosing either purpose, including the same one again, then mounts a brand
 *   new wizard at step 1 with an empty model.
 * - The reset itself submits nothing, emits no completion or error observer
 *   event, and advances nothing: it only replaces the mounted experience. Values
 *   live in the discarded wizard's in-memory model, so nothing survives it, and
 *   a stale browser history entry cannot restore a cleared journey either
 *   (`popstate` for a superseded journey re-mounts clean rather than restoring).
 * - Switching is refused while a submission is in flight and after an accepted
 *   submission, so terminal submission semantics are preserved. The wizard shell
 *   owns that guard and the thank-you panel replaces the card that carries the
 *   control.
 *
 * The optional co-borrower answers (WI-TV-W7-001) live in this same lifecycle
 * and add no step to it. They are shared answers, so a trusted purpose route
 * carries the sanitized secondary values and the opt-in into refinance with the
 * borrower's other answers, and an accepted incoming co-borrower value wins over
 * a retained one on the same key. They are held in the discarded wizard's own
 * model and in the per-journey pending seed, so the destructive reset removes
 * every co-borrower value and the opt-in with everything else; nothing selective
 * is required and nothing secondary can outlive it.
 *
 * Messaging is unchanged: this path is exact-origin only. The wildcard allowlist
 * entry that exists for the local harness is dropped, so a configuration
 * carrying it can neither open the listener to arbitrary senders nor make READY
 * go out with a wildcard target. Accepted values are sanitized by the mounted
 * journey before they are seeded, stay editable, and never auto-advance or
 * auto-submit.
 */
import type { RuntimeConfig } from "../config";
import { installAutofillListener } from "../autofill";
import { buildPrefillAck, buildReadyMessage, notifyEmbedder } from "../messaging";
import type { TelemetrySink } from "../telemetry";
import {
  readRefinancePrefillFromMapped,
  readRefinancePrefillFromSearch,
} from "./refinance-prefill";
import {
  readPurchasePrefillFromMapped,
  readRetainedRefinancePrefillFromPurchaseSnapshot,
} from "./purchase-prefill";
import { MILITARY_COMPONENT_KEY, supersedesMilitaryComponent } from "./borrower-steps";
import { createRefinanceSteps } from "./refinance-steps";
import { inapplicableValueKeys } from "./wizard-machine";
import { renderPurchaseWizard } from "./purchase-wizard";
import { renderRefinanceWizard } from "./refinance-wizard";
import type { RefinanceSubmissionDeps } from "./refinance-submission";
import type { WizardPrefillApplier, WizardSnapshotReader, WizardSwitchProbe } from "./wizard-shell";
import {
  LOAN_PURPOSES,
  renderLoanPurposeSelector,
  type LoanPurpose,
} from "./loan-purpose-selector";
import { LOAN_PURPOSE_QUERY_PARAM } from "./entry-routing";

/** Mapped key carrying the partner's loan-purpose hint. */
const LOAN_PURPOSE_KEY = LOAN_PURPOSE_QUERY_PARAM;

/**
 * Resolves a raw loan-purpose hint to the journey it names, or `undefined` when
 * it names none. Unlike the entry-routing default, an unknown value resolves to
 * nothing here: a prefill that carries no recognizable purpose has no routing or
 * holding effect to report, and reporting one would overstate what was accepted.
 */
function recognizedLoanPurpose(raw: string | undefined): LoanPurpose | undefined {
  const value = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return LOAN_PURPOSES.find((purpose) => purpose === value);
}

/** An empty sanitized seed for every journey the borrower can still choose. */
function emptyPendingSeeds(): Record<LoanPurpose, Record<string, string>> {
  return { purchase: {}, refinance: {} };
}

export interface JourneyMountOptions {
  root: HTMLElement;
  config: RuntimeConfig;
  /**
   * Entry purpose resolved from the URL. An explicit, normalized `purchase` or
   * `refinance` hint direct-routes to that journey's step 1; `undefined` — a
   * missing, blank, or unrecognized hint (WI-TV-W65-001) — opens the neutral
   * two-option selector with no purpose chosen.
   */
  purpose?: LoanPurpose;
  /** Entry search string; refinance answers on it seed the refinance journey. */
  search: string;
  fetchFn?: typeof fetch;
  telemetrySink?: TelemetrySink;
  /** Window used for history state; omitted in environments without one. */
  win?: Window;
}

/** History state written for the mounted journey. */
interface JourneyHistoryState {
  turbovetsJourney: LoanPurpose;
  /**
   * Monotonic id of the mounted journey. A `popstate` carrying an older id is a
   * stale entry for a journey that has since been reset, so it must not restore
   * anything.
   */
  turbovetsJourneyEpoch: number;
}

function isJourneyState(value: unknown): value is JourneyHistoryState {
  if (typeof value !== "object" || value === null) return false;
  const state = value as Record<string, unknown>;
  return (
    typeof state.turbovetsJourneyEpoch === "number" &&
    LOAN_PURPOSES.includes(state.turbovetsJourney as LoanPurpose)
  );
}

function submissionDeps(
  config: RuntimeConfig,
  fetchFn: typeof fetch,
  win?: Window,
  telemetrySink?: TelemetrySink,
): RefinanceSubmissionDeps {
  const deps: RefinanceSubmissionDeps = { config, fetchFn };
  if (telemetrySink) deps.telemetrySink = telemetrySink;
  if (win) deps.win = win;
  return deps;
}

/**
 * Mounts the shared purchase/refinance journey experience and wires the inbound
 * prefill listener, the per-message acknowledgement to the sender's exact
 * origin, and the single READY announcement. Returns a disposer that removes the
 * listener.
 */
export function mountJourneyExperience(options: JourneyMountOptions): () => void {
  const { root, config, search } = options;
  const fetchFn = options.fetchFn ?? fetch;
  const win = options.win;
  const exactOrigins = config.allowedPrefillOrigins.filter((origin) => origin !== "*");

  // Only one journey is mounted at a time; these are its handles. Every reset
  // drops them, so a superseded wizard can neither be seeded nor consulted.
  let applyPrefill: WizardPrefillApplier | undefined;
  let canSwitch: WizardSwitchProbe | undefined;
  let readMountedSnapshot: WizardSnapshotReader | undefined;
  let mountedPurpose: LoanPurpose | undefined;
  let epoch = 0;
  /**
   * Values accepted from an allowed origin while the selector is showing, held
   * so a prefill that arrives between the reset and the borrower's choice can
   * still seed the clean model it is chosen for. Which journey the borrower will
   * pick is unknown, so one sanitized seed is kept per journey: each message is
   * sanitized for both, and only what a journey accepts is merged into that
   * journey's seed. A value a journey rejects therefore cannot erase a valid
   * value an earlier message already contributed to it.
   */
  let pendingSeeds: Record<LoanPurpose, Record<string, string>> = emptyPendingSeeds();

  /** Sanitizes a mapped payload for one journey. */
  function seedFor(
    purpose: LoanPurpose,
    mapped: Readonly<Record<string, string>>,
  ): Record<string, string> {
    return purpose === "refinance"
      ? readRefinancePrefillFromMapped(mapped)
      : readPurchasePrefillFromMapped(mapped);
  }

  /**
   * Holds one message's accepted values for every journey the borrower can still
   * choose and reports the effects it had: the deduplicated union of the values
   * accepted across those journeys, plus a recognized purpose counted once.
   * Values a journey rejected are simply not merged, so its earlier accepted
   * answers survive, and the count describes this message alone.
   */
  function holdForSelection(
    mapped: Readonly<Record<string, string>>,
    purpose: LoanPurpose | undefined,
  ): string[] {
    const effects = new Set<string>();
    for (const available of LOAN_PURPOSES) {
      const accepted = seedFor(available, mapped);
      // Each message is held on top of the ones before it, so the held seed gets
      // the same cross-field rule the mounted wizard applies: a superseding
      // non-affirmative military answer drops the component an earlier message
      // contributed, which would otherwise be carried invisibly into whichever
      // journey the borrower then chooses.
      if (supersedesMilitaryComponent(accepted)) {
        delete pendingSeeds[available][MILITARY_COMPONENT_KEY];
      }
      Object.assign(pendingSeeds[available], accepted);
      for (const key of Object.keys(accepted)) effects.add(key);
    }
    if (purpose !== undefined) effects.add(LOAN_PURPOSE_KEY);
    return [...effects];
  }

  function writeHistoryState(purpose: LoanPurpose): void {
    if (!win?.history) return;
    const state: JourneyHistoryState = {
      turbovetsJourney: purpose,
      turbovetsJourneyEpoch: epoch,
    };
    // The journey is replaced in place rather than pushed: a reset must not
    // leave a history entry that points back at a discarded journey model.
    win.history.replaceState(state, "");
  }

  function mount(purpose: LoanPurpose, initialValues: Record<string, string>): void {
    epoch += 1;
    mountedPurpose = purpose;
    applyPrefill = undefined;
    canSwitch = undefined;
    readMountedSnapshot = undefined;
    pendingSeeds = emptyPendingSeeds();

    const changeLoanType = {
      onActivate: (): void => resetToSelector(),
      onProbe: (probe: WizardSwitchProbe): void => {
        canSwitch = probe;
      },
    };
    const shared = {
      initialValues,
      onPrefillBridge: (apply: WizardPrefillApplier): void => {
        applyPrefill = apply;
      },
      onSnapshotBridge: (read: WizardSnapshotReader): void => {
        readMountedSnapshot = read;
      },
      changeLoanType,
    };

    if (purpose === "refinance") {
      renderRefinanceWizard(root, {
        ...shared,
        submission: submissionDeps(config, fetchFn, win, options.telemetrySink),
      });
    } else {
      // Purchase submits the completed journey through its own purpose-aware
      // submitter (WI-TV-W6-004), which shares the refinance submitter's
      // one-call/terminal/observer/messaging guarantees.
      renderPurchaseWizard(root, {
        ...shared,
        submission: submissionDeps(config, fetchFn, win, options.telemetrySink),
      });
    }

    writeHistoryState(purpose);
  }

  /**
   * Shows the loan-purpose selector with no journey mounted. `afterReset` states
   * plainly that prior answers were cleared; it is `false` only for the initial
   * neutral entry (WI-TV-W65-001), where nothing has been entered yet. Either
   * way no journey is mounted, so a prefill that arrives while the selector is
   * visible is held rather than routed, and choosing a purpose then seeds that
   * journey's held values into a clean model at step 1.
   */
  function showSelector(afterReset: boolean): void {
    epoch += 1;
    mountedPurpose = undefined;
    applyPrefill = undefined;
    canSwitch = undefined;
    readMountedSnapshot = undefined;
    pendingSeeds = emptyPendingSeeds();
    renderLoanPurposeSelector(root, {
      afterReset,
      onSelect: (purpose) => mount(purpose, pendingSeeds[purpose]),
    });
    if (win?.history) win.history.replaceState({}, "");
  }

  /**
   * The destructive reset. Both journey models are discarded with the mounted
   * wizard — there is no retained store to clear selectively, so no answer,
   * error, skip marker, derived value, prefill-derived value, or submission or
   * retry flag can outlive it. Nothing is submitted and no observer is notified.
   */
  function resetToSelector(): void {
    showSelector(true);
  }

  /**
   * Applies an accepted prefill payload to whatever is currently mounted. A
   * payload that asks for refinance while a switchable pre-submit purchase
   * journey is mounted routes into the refinance journey at step 1, carrying the
   * borrower's current shared purchase answers over so a partner switch does not
   * silently discard them (WI-TV-W6-005): the mounted purchase values are read
   * from the explicit in-memory snapshot, sanitized into their refinance-valid
   * equivalents, and the incoming message's sanitized values are merged on top,
   * so an incoming answer wins over a retained one while a valid current answer
   * the message omits survives.
   *
   * While the selector is showing there is no journey to route, and the borrower
   * has just been asked to choose: a message may not answer that question for
   * them. The values are held instead and seeded into whichever clean journey
   * they then select.
   */
  function applyToJourney(mapped: Readonly<Record<string, string>>): string[] {
    const purpose = recognizedLoanPurpose(mapped[LOAN_PURPOSE_KEY]);

    if (mountedPurpose === undefined) return holdForSelection(mapped, purpose);

    if (mountedPurpose !== "refinance" && purpose === "refinance") {
      // A journey in flight or already submitted keeps its answers; only a
      // switchable pre-submit journey may be replaced by a routed prefill.
      if (canSwitch?.() !== true) return [];
      const incoming = readRefinancePrefillFromMapped(mapped);
      const retained = readMountedSnapshot
        ? readRetainedRefinancePrefillFromPurchaseSnapshot(readMountedSnapshot())
        : {};
      const seeded: Record<string, string> = { ...retained, ...incoming };
      // A non-affirmative incoming military answer supersedes a retained
      // component, which the merge would otherwise carry invisibly into
      // refinance behind the borrower's own answer.
      if (supersedesMilitaryComponent(incoming)) delete seeded[MILITARY_COMPONENT_KEY];
      // A value owned by a step the resulting refinance answers make
      // inapplicable — a requested loan amount with no effective cash-out goal
      // (WI-TV-W65-006) — is not seeded and not an effect of this message.
      const dropped = inapplicableValueKeys(createRefinanceSteps(), seeded);
      for (const key of dropped) delete seeded[key];
      mount("refinance", seeded);
      // The acknowledgement describes only this message's effects: the purpose
      // routed the borrower into refinance and each value the message itself
      // carried that the mount accepted. Retained answers are background values
      // the borrower already gave, not effects of this message, and a dropped
      // value had no effect at all, so neither is counted here.
      return [LOAN_PURPOSE_KEY, ...Object.keys(incoming).filter((key) => !dropped.includes(key))];
    }

    return applyPrefill?.(mapped) ?? [];
  }

  // Neutral entry (WI-TV-W65-001): with no explicit purpose the borrower has
  // not chosen yet, so the selector is shown at step 0 rather than mounting a
  // journey. An explicit purpose direct-routes to that journey's step 1, and a
  // refinance entry seeds any refinance answers carried on the query string.
  if (options.purpose === undefined) {
    showSelector(false);
  } else {
    mount(
      options.purpose,
      options.purpose === "refinance" ? readRefinancePrefillFromSearch(search) : {},
    );
  }

  const disposeAutofill = installAutofillListener({
    allowedOrigins: exactOrigins,
    root,
    // There is no legacy form on this path, so accepted values are seeded into
    // the mounted wizard's own value store. Only values from an exact
    // allowlisted origin ever reach the applier.
    apply: (mapped) => applyToJourney(mapped),
    onAck: (outcome, event) => {
      // Acknowledge back to the exact sender origin only (never a wildcard).
      notifyEmbedder(win ?? window, buildPrefillAck(outcome), [event.origin]);
    },
  });

  // A history entry for a journey that has since been reset or replaced must not
  // restore it. The answers only ever existed in the discarded wizard's memory,
  // and re-mounting clean here means even a restored document cannot present
  // stale answers or a stale step.
  const onPopState = (event: PopStateEvent): void => {
    const state: unknown = event.state;
    if (isJourneyState(state) && state.turbovetsJourneyEpoch === epoch) return;
    if (mountedPurpose !== undefined && canSwitch?.() !== true) return;
    if (isJourneyState(state)) mount(state.turbovetsJourney, {});
    else resetToSelector();
  };
  win?.addEventListener("popstate", onPopState);

  notifyEmbedder(
    win ?? window,
    buildReadyMessage({ environment: config.environment }),
    exactOrigins,
  );

  return () => {
    disposeAutofill();
    win?.removeEventListener("popstate", onPopState);
  };
}
